import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  fetchGovernedLibrarySource,
  openGovernedLibrarySourceBlob,
} from "../api/cognitoryLibrarySourceClient";
import { resolveGovernedSourceViewSession } from "../api/cognitoryGovernedSourceViewSessionClient";
import {
  GovernedCitationPdfPageRenderer,
  type GovernedCitationPdfPageMetrics,
} from "./GovernedCitationPdfPageRenderer";
import {
  readGovernedSessionTokenFromLocation,
  toGovernedSourceViewNavigation,
  type GovernedSourceViewNavigation,
} from "../presentation/cognitoryGovernedSourceViewNavigation";
import {
  MAIN_PAGE_COLORS,
  MAIN_PAGE_FONT_STACK,
  MAIN_PAGE_QUIET_BUTTON_STYLE,
  MAIN_PAGE_RADIUS,
  MAIN_PAGE_SECTION_LABEL_STYLE,
} from "../theme/mainPageVisual";

type GovernedSourceViewLoadState =
  | { status: "loading" }
  | {
      status: "ready";
      navigation: GovernedSourceViewNavigation;
      blob: Blob;
      contentType: string;
    }
  | { status: "error"; message: string };

const DEFAULT_PAGE_SCALE = 1;
const NARROW_SCREEN_MAX_WIDTH_PX = 768;
const MIN_PAGE_SCALE = 0.75;
const MAX_PAGE_SCALE = 2.5;
const ZOOM_STEP = 1.15;

const TOOLBAR_BUTTON_STYLE: CSSProperties = {
  border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
  background: MAIN_PAGE_COLORS.quietFill,
  borderRadius: MAIN_PAGE_RADIUS.alert,
  padding: "0.4rem 0.7rem",
  cursor: "pointer",
  color: MAIN_PAGE_COLORS.quietInk,
  fontFamily: MAIN_PAGE_FONT_STACK,
  fontSize: "0.88rem",
  lineHeight: 1.2,
  minWidth: "2.25rem",
};

function clampPageScale(scale: number): number {
  if (scale < MIN_PAGE_SCALE) {
    return MIN_PAGE_SCALE;
  }
  if (scale > MAX_PAGE_SCALE) {
    return MAX_PAGE_SCALE;
  }
  return scale;
}

function formatZoomPercent(scale: number): string {
  return `${Math.round(scale * 100)}%`;
}

export default function CognitoryGovernedSourceViewPage(): JSX.Element {
  const location = useLocation();
  const params = useParams();
  const cognitoryId = params.cognitoryId ?? "";
  const sessionToken = readGovernedSessionTokenFromLocation(location.search);
  const documentViewportRef = useRef<HTMLDivElement | null>(null);
  const hasAutoFitRef = useRef(false);
  const [loadState, setLoadState] = useState<GovernedSourceViewLoadState>({
    status: "loading",
  });
  const [pageScale, setPageScale] = useState(DEFAULT_PAGE_SCALE);
  const [pageMetrics, setPageMetrics] =
    useState<GovernedCitationPdfPageMetrics | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!sessionToken || !cognitoryId) {
      setLoadState({
        status: "error",
        message: "Governed source view is unavailable.",
      });
      return;
    }

    void (async () => {
      const resolved = await resolveGovernedSourceViewSession({
        cognitoryId,
        governedSession: sessionToken,
      });
      if (cancelled) {
        return;
      }
      if (resolved.kind !== "resolved") {
        setLoadState({
          status: "error",
          message: "Unable to open the governed source document.",
        });
        return;
      }

      const navigation = toGovernedSourceViewNavigation({
        cognitoryId,
        citationNumber: resolved.citationNumber,
        documentId: resolved.documentId,
        documentVersionId: resolved.documentVersionId,
        documentTitle: resolved.documentTitle,
        pageNumber: resolved.pageNumber,
        citedPassage: resolved.citedPassage,
      });

      const result = await fetchGovernedLibrarySource({
        cognitoryId: navigation.cognitoryId,
        documentId: navigation.documentId,
        documentVersionId: navigation.documentVersionId,
      });
      if (cancelled) {
        return;
      }
      if (result.kind !== "opened") {
        setLoadState({
          status: "error",
          message: "Unable to open the governed source document.",
        });
        return;
      }
      setLoadState({
        status: "ready",
        navigation,
        blob: result.blob,
        contentType: result.contentType,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [cognitoryId, sessionToken]);

  const handlePageMetrics = useCallback(
    (metrics: GovernedCitationPdfPageMetrics) => {
      setPageMetrics(metrics);
    },
    [],
  );

  const fitPageToWidth = useCallback(() => {
    const container = documentViewportRef.current;
    if (!container || !pageMetrics) {
      return;
    }
    const availableWidth = container.clientWidth - 32;
    const unscaledWidth = pageMetrics.width / pageMetrics.scale;
    if (unscaledWidth <= 0 || availableWidth <= 0) {
      return;
    }
    setPageScale(clampPageScale(availableWidth / unscaledWidth));
  }, [pageMetrics]);

  useEffect(() => {
    if (!pageMetrics || hasAutoFitRef.current) {
      return;
    }
    const isNarrowScreen =
      typeof window !== "undefined" &&
      window.matchMedia(`(max-width: ${NARROW_SCREEN_MAX_WIDTH_PX}px)`).matches;
    if (isNarrowScreen) {
      fitPageToWidth();
    }
    hasAutoFitRef.current = true;
  }, [fitPageToWidth, pageMetrics]);

  const goBackToAnswer = () => {
    window.close();
  };

  const zoomOut = () => {
    setPageScale(current => clampPageScale(current / ZOOM_STEP));
  };

  const zoomIn = () => {
    setPageScale(current => clampPageScale(current * ZOOM_STEP));
  };

  if (loadState.status === "loading") {
    return (
      <div
        data-testid="cognitory-governed-source-view"
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          fontFamily: MAIN_PAGE_FONT_STACK,
          color: MAIN_PAGE_COLORS.muted,
          background: MAIN_PAGE_COLORS.pageBackground,
        }}
      >
        Loading governed source…
      </div>
    );
  }

  if (loadState.status === "error") {
    return (
      <div
        data-testid="cognitory-governed-source-view"
        style={{
          minHeight: "100vh",
          padding: "2rem",
          fontFamily: MAIN_PAGE_FONT_STACK,
          background: MAIN_PAGE_COLORS.pageBackground,
        }}
      >
        <p data-testid="cognitory-governed-source-view-error">
          {loadState.message}
        </p>
        <button
          type="button"
          data-testid="cognitory-governed-source-view-back"
          style={MAIN_PAGE_QUIET_BUTTON_STYLE}
          onClick={goBackToAnswer}
        >
          Back to answer
        </button>
      </div>
    );
  }

  const { navigation, blob, contentType } = loadState;

  return (
    <div
      data-testid="cognitory-governed-source-view"
      style={{
        minHeight: "100vh",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: MAIN_PAGE_COLORS.pageBackground,
        fontFamily: MAIN_PAGE_FONT_STACK,
        color: MAIN_PAGE_COLORS.slate,
        overflow: "hidden",
      }}
    >
      <header
        data-testid="cognitory-governed-source-view-toolbar"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          background: "rgba(255, 255, 255, 0.94)",
          borderBottom: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
        }}
      >
        <div
          style={{
            maxWidth: "1280px",
            margin: "0 auto",
            padding: "0.75rem clamp(0.85rem, 2.5vw, 1.5rem)",
            display: "flex",
            flexWrap: "wrap",
            gap: "0.75rem",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              minWidth: 0,
              flex: "1 1 220px",
            }}
          >
            <button
              type="button"
              data-testid="cognitory-governed-source-view-back"
              onClick={goBackToAnswer}
              style={{
                ...MAIN_PAGE_QUIET_BUTTON_STYLE,
                marginTop: 0,
                flexShrink: 0,
              }}
            >
              Back to answer
            </button>
            <div style={{ minWidth: 0 }}>
              <p style={{ ...MAIN_PAGE_SECTION_LABEL_STYLE, marginBottom: 2 }}>
                Governed document
              </p>
              <h1
                data-testid="cognitory-governed-source-view-title"
                style={{
                  margin: 0,
                  fontSize: "clamp(0.95rem, 2vw, 1.15rem)",
                  fontWeight: 500,
                  lineHeight: 1.35,
                  color: MAIN_PAGE_COLORS.inkStrong,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                [{navigation.citationNumber}] {navigation.documentTitle}
              </h1>
              <p
                data-testid="cognitory-citation-pdf-highlight-page"
                style={{
                  margin: "0.2rem 0 0",
                  color: MAIN_PAGE_COLORS.soft,
                  fontSize: "0.86rem",
                }}
              >
                Page {navigation.pageNumber}
              </p>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "0.45rem",
              justifyContent: "flex-end",
              flex: "0 1 auto",
            }}
          >
            <div
              data-testid="cognitory-governed-source-view-zoom"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.35rem",
                border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
                borderRadius: MAIN_PAGE_RADIUS.alert,
                background: "rgba(255, 255, 255, 0.72)",
                padding: "0.2rem",
              }}
            >
              <button
                type="button"
                data-testid="cognitory-governed-source-view-zoom-out"
                aria-label="Zoom out"
                onClick={zoomOut}
                style={TOOLBAR_BUTTON_STYLE}
              >
                −
              </button>
              <span
                data-testid="cognitory-governed-source-view-zoom-label"
                style={{
                  minWidth: "3.25rem",
                  textAlign: "center",
                  fontSize: "0.86rem",
                  color: MAIN_PAGE_COLORS.quietInk,
                }}
              >
                {formatZoomPercent(pageScale)}
              </span>
              <button
                type="button"
                data-testid="cognitory-governed-source-view-zoom-in"
                aria-label="Zoom in"
                onClick={zoomIn}
                style={TOOLBAR_BUTTON_STYLE}
              >
                +
              </button>
            </div>

            <button
              type="button"
              data-testid="cognitory-governed-source-view-fit-width"
              onClick={fitPageToWidth}
              style={TOOLBAR_BUTTON_STYLE}
            >
              Fit width
            </button>

            <button
              type="button"
              data-testid="cognitory-citation-pdf-highlight-open-native"
              onClick={() => {
                openGovernedLibrarySourceBlob({
                  blob,
                  contentType,
                  pageNumber: navigation.pageNumber,
                  citedPassage: navigation.citedPassage,
                });
              }}
              style={{
                ...TOOLBAR_BUTTON_STYLE,
                color: MAIN_PAGE_COLORS.link,
                whiteSpace: "nowrap",
              }}
            >
              Open original PDF — page {navigation.pageNumber}
            </button>
          </div>
        </div>
      </header>

      <main
        ref={documentViewportRef}
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          maxWidth: "1280px",
          width: "100%",
          margin: "0 auto",
          padding: "1rem clamp(0.85rem, 2.5vw, 1.5rem) 2rem",
        }}
      >
        <GovernedCitationPdfPageRenderer
          blob={blob}
          pageNumber={navigation.pageNumber}
          citedPassage={navigation.citedPassage}
          pageScale={pageScale}
          onPageMetrics={handlePageMetrics}
        />
      </main>
    </div>
  );
}
